import { formatDistanceToNow, subDays } from 'date-fns';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Play,
  RefreshCw,
  RotateCw,
  Settings,
  Square,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  CrashingOutExecutionHistory,
  type StepTrace,
} from '@/components/agentflow/CrashingOutExecutionHistory';
import { CrashingOutRunDetailsModal } from '@/components/agentflow/CrashingOutRunDetailsModal';
import {
  type CrashingOutSource,
  useCrashingOutSource,
} from '@/components/agentflow/useCrashingOutSource';
import { CoverageCardRedesign } from '@/components/coverage/shared/CoverageCardRedesign';
import { TaskDetail } from '@/components/tasks/task-detail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { isCrashingOutEnabledForUser } from '@/lib/featureFlags';
import { cn } from '@/lib/utils';
import {
  type CrashingOutPendingReview,
  useCancelCrashingOutMutation,
  useLazyGetCrashingOutPendingReviewsQuery,
  useLazyGetCrashingOutRunStatusQuery,
  useStartCrashingOutMutation,
} from '@/services/api/agentflowService';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import type { QuantModeRun } from '@/services/api/quantModeService';
import { useGetQuantModeRunsQuery } from '@/services/api/quantModeService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import axiosInstance from '@/services/axiosConfig';
import type { Task } from '@/services/types/client-intake-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';
import { selectUser } from '@/state/selectors/root-selectors';
import { buildTemporalWorkflowUrl } from '@/utils/temporalUtils';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  'running',
  'completed',
  'failed',
  'timed_out',
  'escalated',
  'terminated',
  'awaiting_review',
] as const;

const ACTIVE_QUANT_RUN_STATUSES = new Set(['initializing', 'running', 'awaiting_review']);

/** Format a Date as YYYY-MM-DD for date inputs. */
const toDateString = (date: Date): string => date.toISOString().slice(0, 10);

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'running':
      return 'bg-blue-100 text-blue-700';
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    case 'timed_out':
      return 'bg-orange-100 text-orange-700';
    case 'escalated':
      return 'bg-purple-100 text-purple-700';
    case 'terminated':
      return 'bg-zinc-200 text-zinc-700';
    case 'awaiting_review':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-zinc-100 text-zinc-600';
  }
};

const formatStarted = (iso: string): string => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
};

const ACTIVE_RUN_STATUSES = new Set(['initializing', 'running', 'awaiting_review']);

const rowBgClass = (run: QuantModeRun): string => {
  const needsReview =
    ACTIVE_RUN_STATUSES.has(run.status) &&
    run.latest_tool === 'escalate_for_human_review' &&
    run.latest_tool_status === 'running';
  if (needsReview) return 'bg-red-50/60';
  switch (run.status) {
    case 'failed':
    case 'timed_out':
      return 'bg-red-50/60';
    case 'completed':
      return 'bg-emerald-50/50';
    case 'running':
      return 'bg-amber-50/50';
    default:
      return '';
  }
};

const LatestToolBadge: React.FC<{ run: QuantModeRun }> = ({ run }) => {
  if (!run.latest_tool) {
    return <span className="text-xs text-zinc-500">—</span>;
  }

  const isHumanReviewTool = run.latest_tool === 'escalate_for_human_review';
  const isRunActive = ACTIVE_RUN_STATUSES.has(run.status);
  const isHumanReviewRunning = isRunActive && isHumanReviewTool && run.latest_tool_status === 'running';
  return (
    <Badge
      className={cn(
        'text-[10px] font-mono',
        isHumanReviewTool && isRunActive ? 'bg-amber-100 text-amber-700' : 'bg-indigo-50 text-indigo-600',
        isHumanReviewRunning && 'animate-pulse',
      )}
    >
      {isHumanReviewRunning ? 'awaiting review' : run.latest_tool}
    </Badge>
  );
};

const formatDuration = (startedAt: string, closedAt: string | null): string => {
  if (!closedAt) return 'In progress';
  try {
    const ms = new Date(closedAt).getTime() - new Date(startedAt).getTime();
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60_000)}m`;
  } catch {
    return '—';
  }
};

const formatCompletionTime = (closedAt: string | null): string => {
  if (!closedAt) return '—';
  try {
    return new Date(closedAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
};

interface SchemaProperty {
  type?: string;
  default?: unknown;
}

const getDefaultFormData = (schema: Record<string, unknown> | null | undefined) => {
  const defaults: Record<string, unknown> = {};
  const properties = (schema?.properties || {}) as Record<string, SchemaProperty>;
  Object.entries(properties).forEach(([key, property]) => {
    if (property.default !== undefined) {
      defaults[key] = property.default;
    } else if (property.type === 'boolean') {
      defaults[key] = false;
    } else {
      defaults[key] = '';
    }
  });
  return defaults;
};

const DEFAULT_STARTED_AFTER = toDateString(subDays(new Date(), 7));
const DEFAULT_STARTED_BEFORE = toDateString(new Date());

// ---------------------------------------------------------------------------
// Coverage Run Group (coverage card + compact run rows)
// ---------------------------------------------------------------------------

interface BaseCoverageRunGroupProps {
  coverageId: string;
  matterId: string;
  matterName: string;
  runs: QuantModeRun[];
  onOpenRunDetail: (run: QuantModeRun) => void;
  onOpenTasks: (run: QuantModeRun) => void;
  onStopRun: (run: QuantModeRun) => void;
  onRestartRun: (run: QuantModeRun) => void;
  stoppingRunId: string | null;
  isFetching: boolean;
}

const TemporalCoverageRunGroup: React.FC<BaseCoverageRunGroupProps> = ({
  coverageId,
  matterId,
  matterName,
  runs,
  onOpenRunDetail,
  onOpenTasks,
  onStopRun,
  onRestartRun,
  stoppingRunId,
  isFetching,
}) => {
  const { data: coverages, isLoading: coverageLoading } = useGetCoveragesQuery({
    matterId,
    includeShared: true,
  });
  const coverage = coverages?.find((c) => c.id === coverageId);

  return (
    <div className="space-y-2">
      {/* Matter link */}
      <div className="flex items-center gap-2">
        <Link
          to={`/matters/${matterId}/coverage`}
          className="text-sm font-medium text-violet-700 hover:underline"
        >
          {matterName}
        </Link>
        <span className="text-xs text-zinc-500">
          {runs.length} run{runs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Coverage Card */}
      {coverageLoading ? (
        <Skeleton className="h-32 w-full rounded-xl" />
      ) : coverage ? (
        <CoverageCardRedesign coverage={coverage} matterId={matterId} readOnly forceShowProgressStepper />
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-800">
              {runs[0]?.coverage_name || 'Unknown Coverage'}
            </span>
            <Badge className="bg-zinc-100 text-zinc-600 text-[10px]">
              {runs[0]?.coverage_category}
            </Badge>
          </div>
        </div>
      )}

      {/* Runs table */}
      <div className="rounded border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Status</th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">
                Latest Tool
              </th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Started</th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Duration</th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">
                Completion Time
              </th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">
                Initiated By
              </th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr
                key={run.run_id}
                className={cn(
                  'border-b border-zinc-100 hover:bg-zinc-100/50 transition-colors',
                  rowBgClass(run),
                  isFetching && 'opacity-60',
                )}
              >
                <td className="px-3 py-1.5">
                  <Badge className={cn('text-[10px]', statusBadgeClass(run.status))}>
                    {run.status.replace('_', ' ')}
                  </Badge>
                </td>
                <td className="px-3 py-1.5">
                  {run.latest_tool ? (
                    <Badge
                      className={cn(
                        'text-[10px] font-mono',
                        ACTIVE_RUN_STATUSES.has(run.status) &&
                          run.latest_tool === 'escalate_for_human_review' &&
                          run.latest_tool_status === 'running'
                          ? 'bg-amber-100 text-amber-700 animate-pulse'
                          : 'bg-indigo-50 text-indigo-600',
                      )}
                    >
                      {ACTIVE_RUN_STATUSES.has(run.status) &&
                      run.latest_tool === 'escalate_for_human_review' &&
                      run.latest_tool_status === 'running'
                        ? 'awaiting review'
                        : run.latest_tool}
                    </Badge>
                  ) : (
                    <span className="text-xs text-zinc-500">—</span>
                  )}
                </td>
                  <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                    {formatStarted(run.started_at)}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                    {formatDuration(run.started_at, run.closed_at)}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                    {formatCompletionTime(run.closed_at)}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                    {run.initiated_by_user_name || '—'}
                  </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <button
                      type="button"
                      className="text-xs text-violet-600 hover:underline font-medium"
                      onClick={() => onOpenRunDetail(run)}
                    >
                      Agent
                    </button>
                    <button
                      type="button"
                      className="text-xs text-zinc-500 hover:underline"
                      onClick={() => onOpenTasks(run)}
                    >
                      Tasks
                    </button>
                    <a
                      href={buildTemporalWorkflowUrl(run.workflow_id, run.run_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-zinc-500 hover:underline"
                    >
                      Temporal
                    </a>
                    {run.status === 'running' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-6 gap-1 px-2 text-[11px]"
                        disabled={stoppingRunId === run.run_id}
                        onClick={() => onStopRun(run)}
                      >
                        <Square className="h-3 w-3" />
                        {stoppingRunId === run.run_id ? 'Stopping...' : 'Stop'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 gap-1 px-2 text-[11px]"
                      onClick={() => onRestartRun(run)}
                    >
                      <RotateCw className="h-3 w-3" />
                      Restart
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface PostgresCoverageRunGroupProps extends BaseCoverageRunGroupProps {
  onStartRun: (matterId: string, coverageId: string) => void;
  onStartRunWithInstructions: (target: {
    matterId: string;
    coverageId: string;
    coverageName: string;
  }) => void;
  isStarting: boolean;
}

const PostgresCoverageRunGroup: React.FC<PostgresCoverageRunGroupProps> = ({
  coverageId,
  matterId,
  matterName,
  runs,
  onStartRun,
  onStartRunWithInstructions,
  isStarting,
  onOpenRunDetail,
  onOpenTasks,
  onStopRun,
  onRestartRun,
  stoppingRunId,
  isFetching,
}) => {
  const { data: coverages, isLoading: coverageLoading } = useGetCoveragesQuery({
    matterId,
    includeShared: true,
  });
  const coverage = coverages?.find((c) => c.id === coverageId);
  const coverageName = runs[0]?.coverage_name || 'Unknown Coverage';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Link
          to={`/matters/${matterId}/coverage`}
          className="text-sm font-medium text-violet-700 hover:underline"
        >
          {matterName}
        </Link>
        <span className="text-xs text-zinc-500">
          {runs.length} run{runs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {coverageLoading ? (
        <Skeleton className="h-32 w-full rounded-xl" />
      ) : coverage ? (
        <CoverageCardRedesign coverage={coverage} matterId={matterId} readOnly forceShowProgressStepper />
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-800">{coverageName}</span>
            <Badge className="bg-zinc-100 text-zinc-600 text-[10px]">
              {runs[0]?.coverage_category}
            </Badge>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={isStarting}
          onClick={() =>
            onStartRunWithInstructions({
              matterId,
              coverageId,
              coverageName,
            })
          }
        >
          Start w/ Instructions
        </Button>
        <Button
          size="sm"
          className="h-7 gap-1.5 text-xs"
          disabled={isStarting}
          onClick={() => onStartRun(matterId, coverageId)}
        >
          <Play className="h-3.5 w-3.5" />
          {isStarting ? 'Starting...' : 'Start Run'}
        </Button>
      </div>

      <div className="rounded border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Status</th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">
                Latest Tool
              </th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Started</th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Duration</th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">
                Completion Time
              </th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">
                Initiated By
              </th>
              <th className="text-left px-3 py-1.5 text-xs font-medium text-zinc-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr
                key={run.run_id}
                className={cn(
                  'border-b border-zinc-100 hover:bg-zinc-100/50 transition-colors',
                  rowBgClass(run),
                  isFetching && 'opacity-60',
                )}
              >
                <td className="px-3 py-1.5">
                  <Badge className={cn('text-[10px]', statusBadgeClass(run.status))}>
                    {run.status.replace('_', ' ')}
                  </Badge>
                </td>
                <td className="px-3 py-1.5">
                  <LatestToolBadge run={run} />
                </td>
                <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                  {formatStarted(run.started_at)}
                </td>
                <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                  {formatDuration(run.started_at, run.closed_at)}
                </td>
                <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                  {formatCompletionTime(run.closed_at)}
                </td>
                <td className="px-3 py-1.5 text-xs text-zinc-600 whitespace-nowrap">
                  {run.initiated_by_user_name || '—'}
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <button
                      type="button"
                      className="text-xs text-violet-600 hover:underline font-medium"
                      onClick={() => onOpenRunDetail(run)}
                    >
                      Agent
                    </button>
                    <button
                      type="button"
                      className="text-xs text-zinc-500 hover:underline"
                      onClick={() => onOpenTasks(run)}
                    >
                      Tasks
                    </button>
                    <a
                      href={buildTemporalWorkflowUrl(run.workflow_id, run.run_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-zinc-500 hover:underline"
                    >
                      Temporal
                    </a>
                    {ACTIVE_QUANT_RUN_STATUSES.has(run.status) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-6 gap-1 px-2 text-[11px]"
                        disabled={stoppingRunId === run.run_id}
                        onClick={() => onStopRun(run)}
                      >
                        <Square className="h-3 w-3" />
                        {stoppingRunId === run.run_id ? 'Stopping...' : 'Stop'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 gap-1 px-2 text-[11px]"
                      onClick={() => onRestartRun(run)}
                    >
                      <RotateCw className="h-3 w-3" />
                      Restart
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Temporal Run Detail Dialog (staging-style)
// ---------------------------------------------------------------------------

interface RunDetailDialogProps {
  run: QuantModeRun | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RunDetailDialog: React.FC<RunDetailDialogProps> = ({ run, open, onOpenChange }) => {
  const [traces, setTraces] = useState<StepTrace[]>([]);
  const [runStatus, setRunStatus] = useState<string | undefined>();
  const [pendingReviews, setPendingReviews] = useState<CrashingOutPendingReview[]>([]);
  const [reviewFormData, setReviewFormData] = useState<Record<string, Record<string, unknown>>>({});
  const [submittingReviewId, setSubmittingReviewId] = useState<string | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [fetchRunStatus] = useLazyGetCrashingOutRunStatusQuery();
  const [fetchPendingReviews] = useLazyGetCrashingOutPendingReviewsQuery();

  const loadRunDetail = useCallback(
    async (currentRun: QuantModeRun) => {
      if (!currentRun.matter_id || !currentRun.coverage_id || !currentRun.run_id) return;
      setIsLoadingDetail(true);
      try {
        const [statusResult, reviewsResult] = await Promise.all([
          fetchRunStatus({
            matterId: currentRun.matter_id,
            coverageId: currentRun.coverage_id,
            runId: currentRun.run_id,
          }).unwrap(),
          fetchPendingReviews({
            matterId: currentRun.matter_id,
            coverageId: currentRun.coverage_id,
          }).unwrap(),
        ]);

        setTraces((statusResult.traces || []) as unknown as StepTrace[]);
        setRunStatus(statusResult.workflow_status);
        setPendingReviews(reviewsResult.reviews || []);

        const nextFormData: Record<string, Record<string, unknown>> = {};
        (reviewsResult.reviews || []).forEach((review) => {
          nextFormData[review.approval_id] = getDefaultFormData(review.response_schema);
        });
        setReviewFormData(nextFormData);
      } catch {
        toast.error('Failed to load run details');
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [fetchPendingReviews, fetchRunStatus],
  );

  useEffect(() => {
    if (open && run) {
      loadRunDetail(run);
    }
    if (!open) {
      setTraces([]);
      setPendingReviews([]);
      setReviewFormData({});
      setRunStatus(undefined);
    }
  }, [loadRunDetail, open, run]);

  useEffect(() => {
    if (!isLoadingDetail && traces.length > 0 && bottomRef.current) {
      const timer = setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isLoadingDetail, traces.length]);

  const handleReviewFieldChange = useCallback(
    (approvalId: string, field: string, value: unknown) => {
      setReviewFormData((prev) => ({
        ...prev,
        [approvalId]: { ...(prev[approvalId] || {}), [field]: value },
      }));
    },
    [],
  );

  const handleSubmitReview = useCallback(
    async (approvalId: string) => {
      if (!run?.matter_id) return;
      try {
        setSubmittingReviewId(approvalId);
        await axiosInstance.post(`/api/agentflow/eval/${run.matter_id}/${approvalId}/submit/`, {
          action: 'submit',
          modified_data: reviewFormData[approvalId] || {},
        });
        toast.success('Review submitted');
        await loadRunDetail(run);
      } catch {
        toast.error('Failed to submit review');
      } finally {
        setSubmittingReviewId(null);
      }
    },
    [loadRunDetail, reviewFormData, run],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">
            {run?.matter_name} — {run?.coverage_name}
          </DialogTitle>
        </DialogHeader>
        {isLoadingDetail ? (
          <div className="space-y-3 py-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            <CrashingOutExecutionHistory
              traces={traces}
              runStatus={runStatus}
              matterId={run?.matter_id}
              legacyTemporalStyle
              pendingReviews={pendingReviews}
              reviewFormDataByApproval={reviewFormData}
              submittingReviewId={submittingReviewId}
              onReviewFieldChange={handleReviewFieldChange}
              onSubmitReview={handleSubmitReview}
              defaultExpandLast
            />
            <div ref={bottomRef} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Coverage Tasks Dialog
// ---------------------------------------------------------------------------

interface CoverageTasksDialogProps {
  matterId: string;
  coverageId: string;
  coverageName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CoverageTasksDialog: React.FC<CoverageTasksDialogProps> = ({
  matterId,
  coverageId,
  coverageName,
  open,
  onOpenChange,
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const { data: tasksResponse, isLoading } = useGetTasksQuery(
    { matter: matterId, limit: 200 },
    { skip: !open },
  );

  const coverageTasks = useMemo(() => {
    const tasks = (tasksResponse?.results as Task[]) || [];
    return tasks.filter(
      (t) => (t as any).coverage_id === coverageId || t.coverage?.id === coverageId,
    );
  }, [tasksResponse?.results, coverageId]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[80vh] sm:max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Tasks — {coverageName}</DialogTitle>
          </DialogHeader>
          {isLoading ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : coverageTasks.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">
              No tasks for this coverage.
            </p>
          ) : (
            <div className="space-y-1">
              {coverageTasks.map((task) => {
                const isDone = task.status?.canonical_status === CanonicalTaskStatus.COMPLETED;
                return (
                  <button
                    key={task.id}
                    type="button"
                    className="w-full flex items-center gap-2 rounded border border-zinc-200 px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
                    onClick={() => setSelectedTaskId(task.id)}
                  >
                    <div
                      className={cn(
                        'flex items-center justify-center w-5 h-5 rounded-full shrink-0',
                        isDone
                          ? 'bg-emerald-500 text-white'
                          : 'border-2 border-zinc-300',
                      )}
                    >
                      {isDone && <Check className="h-3 w-3" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-800 truncate">
                        {task.type?.name || task.name || 'Task'}
                      </div>
                      {task.assignee && (
                        <div className="text-xs text-zinc-500">
                          {task.assignee.first_name} {task.assignee.last_name}
                        </div>
                      )}
                    </div>
                    <Badge
                      className={cn(
                        'text-[10px] shrink-0',
                        isDone
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-zinc-100 text-zinc-600',
                      )}
                    >
                      {task.status?.name || 'Unknown'}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task detail side panel */}
      {selectedTaskId && (
        <Dialog open onOpenChange={() => setSelectedTaskId(null)} modal={false}>
          <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
            <DialogTitle className="sr-only">Task Details</DialogTitle>
            <TaskDetail
              taskId={selectedTaskId}
              matterId={matterId}
              onClose={() => setSelectedTaskId(null)}
            />
          </RightDialogContent>
        </Dialog>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const TemporalSourceSelector: React.FC<{
  source: CrashingOutSource;
  setSource: (nextSource: CrashingOutSource) => void;
}> = ({ source, setSource }) => (
  <Select value={source} onValueChange={(value) => setSource(value as CrashingOutSource)}>
    <SelectTrigger className="h-8 w-28 text-xs">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="temporal">Temporal</SelectItem>
      <SelectItem value="postgres">Postgres</SelectItem>
    </SelectContent>
  </Select>
);

const TemporalQuantModePageContent: React.FC = () => {
  const user = useSelector(selectUser);
  const isEnabled = isCrashingOutEnabledForUser(user?.email);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRun, setSelectedRun] = useState<QuantModeRun | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tasksRun, setTasksRun] = useState<QuantModeRun | null>(null);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);
  const [restartRun, setRestartRun] = useState<QuantModeRun | null>(null);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [restartInstructions, setRestartInstructions] = useState('');
  const [isRestarting, setIsRestarting] = useState(false);

  const status = searchParams.get('status') || '';
  const userId = searchParams.get('user_id') || '';
  const taskAssigneeId = searchParams.get('task_assignee_id') || '';
  const startedAfter = searchParams.get('started_after') ?? DEFAULT_STARTED_AFTER;
  const startedBefore = searchParams.get('started_before') ?? DEFAULT_STARTED_BEFORE;
  const offset = parseInt(searchParams.get('offset') || '0', 10);

  const queryParams = useMemo(
    () => ({
      status: status || undefined,
      user_id: userId || undefined,
      task_assignee_id: taskAssigneeId || undefined,
      started_after: startedAfter || undefined,
      started_before: startedBefore || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [status, userId, taskAssigneeId, startedAfter, startedBefore, offset],
  );

  const { data: staffUsers = [] } = useGetStaffUsersQuery(undefined, { skip: !isEnabled });

  const sortedStaffUsers = useMemo(
    () =>
      [...staffUsers].sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
      ),
    [staffUsers],
  );

  const { data, isLoading, isFetching, error, refetch } = useGetQuantModeRunsQuery(queryParams, {
    skip: !isEnabled,
  });

  const [cancelCrashingOut] = useCancelCrashingOutMutation();
  const [startCrashingOut] = useStartCrashingOutMutation();
  const [stoppingRunId, setStoppingRunId] = useState<string | null>(null);

  const handleStopRun = useCallback(
    async (run: QuantModeRun) => {
      if (!run.matter_id || !run.coverage_id) return;
      setStoppingRunId(run.run_id);
      try {
        await cancelCrashingOut({
          matterId: run.matter_id,
          coverageId: run.coverage_id,
          request: { reason: 'Stopped from Quant Mode' },
        }).unwrap();
        toast.success('Workflow stopped');
        refetch();
      } catch {
        toast.error('Failed to stop workflow');
      } finally {
        setStoppingRunId(null);
      }
    },
    [cancelCrashingOut, refetch],
  );

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== 'offset') {
      next.delete('offset');
    }
    setSearchParams(next);
  };

  const handleOpenRunDetail = (run: QuantModeRun) => {
    setSelectedRun(run);
    setDialogOpen(true);
  };

  const handleOpenRestartDialog = (run: QuantModeRun) => {
    setRestartRun(run);
    setRestartInstructions('');
    setRestartDialogOpen(true);
  };

  const handleRestartRun = useCallback(async () => {
    if (!restartRun?.matter_id || !restartRun?.coverage_id) return;
    setIsRestarting(true);
    let didCancel = false;
    try {
      if (restartRun.status === 'running') {
        await cancelCrashingOut({
          matterId: restartRun.matter_id,
          coverageId: restartRun.coverage_id,
          request: { reason: 'Restarted from Quant Mode with new instructions' },
        }).unwrap();
        didCancel = true;
      }
      const request = restartInstructions.trim()
        ? { instructions: restartInstructions.trim() }
        : {};
      await startCrashingOut({
        matterId: restartRun.matter_id,
        coverageId: restartRun.coverage_id,
        request,
      }).unwrap();
      toast.success('Crashing Out restarted');
      setRestartDialogOpen(false);
      setRestartRun(null);
      setRestartInstructions('');
      refetch();
    } catch {
      if (didCancel) {
        toast.error('Workflow was stopped but failed to start a new one. Please restart manually.');
        refetch();
      } else {
        toast.error('Failed to restart Crashing Out');
      }
    } finally {
      setIsRestarting(false);
    }
  }, [restartRun, restartInstructions, cancelCrashingOut, startCrashingOut, refetch]);

  const total = data?.count || 0;
  const showingStart = total > 0 ? offset + 1 : 0;
  const showingEnd = Math.min(offset + PAGE_SIZE, total);

  const groupedByCoverage = useMemo(() => {
    const groups = new Map<
      string,
      { coverageId: string; matterId: string; matterName: string; runs: QuantModeRun[] }
    >();
    (data?.results || []).forEach((run) => {
      const key = `${run.matter_id}:${run.coverage_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          coverageId: run.coverage_id,
          matterId: run.matter_id,
          matterName: run.matter_name,
          runs: [],
        });
      }
      const group = groups.get(key);
      if (group) group.runs.push(run);
    });
    return Array.from(groups.values());
  }, [data?.results]);

  if (!isEnabled) {
    return <Navigate to="/matters" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-900">Quant Mode</h1>
          <p className="text-sm text-zinc-600">
            Monitor all Crashing Out runs across matters.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          disabled={isFetching}
          onClick={() => refetch()}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant={!status ? 'default' : 'outline'}
            className="h-7 text-xs"
            onClick={() => setParam('status', '')}
          >
            All
          </Button>
          {STATUS_OPTIONS.map((currentStatus) => (
            <Button
              key={currentStatus}
              size="sm"
              variant={status === currentStatus ? 'default' : 'outline'}
              className="h-7 text-xs capitalize"
              onClick={() => setParam('status', status === currentStatus ? '' : currentStatus)}
            >
              {currentStatus.replace('_', ' ')}
            </Button>
          ))}
        </div>

        <Select
          value={userId || 'all'}
          onValueChange={(value) => setParam('user_id', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="h-7 w-36 sm:w-48 text-xs">
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {sortedStaffUsers.map((staffUser) => (
              <SelectItem key={staffUser.id} value={staffUser.id}>
                {staffUser.first_name} {staffUser.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={taskAssigneeId || 'all'}
          onValueChange={(value) => setParam('task_assignee_id', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="h-7 w-36 sm:w-48 text-xs">
            <SelectValue placeholder="OC Assignee: All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">OC Assignee: All</SelectItem>
            {sortedStaffUsers.map((staffUser) => (
              <SelectItem key={staffUser.id} value={staffUser.id}>
                {staffUser.first_name} {staffUser.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={startedAfter}
          onChange={(event) => setParam('started_after', event.target.value)}
          className="h-7 w-32 sm:w-36 text-xs"
          placeholder="From"
        />
        <Input
          type="date"
          value={startedBefore}
          onChange={(event) => setParam('started_before', event.target.value)}
          className="h-7 w-32 sm:w-36 text-xs"
          placeholder="To"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load Quant Mode data. Check the browser console for details.
        </div>
      ) : (
        <>
          {groupedByCoverage.length === 0 ? (
            <div className="rounded border border-zinc-200 p-8 text-center text-sm text-zinc-500">
              No runs found.
            </div>
          ) : (
            <div className="space-y-6">
              {groupedByCoverage.map((group) => (
                <TemporalCoverageRunGroup
                  key={`${group.matterId}:${group.coverageId}`}
                  coverageId={group.coverageId}
                  matterId={group.matterId}
                  matterName={group.matterName}
                  runs={group.runs}
                  onOpenRunDetail={handleOpenRunDetail}
                  onOpenTasks={(run) => {
                    setTasksRun(run);
                    setTasksDialogOpen(true);
                  }}
                  onStopRun={handleStopRun}
                  onRestartRun={handleOpenRestartDialog}
                  stoppingRunId={stoppingRunId}
                  isFetching={isFetching}
                />
              ))}
            </div>
          )}

          {total > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">
                {showingStart}–{showingEnd} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  disabled={offset === 0}
                  onClick={() => setParam('offset', String(Math.max(0, offset - PAGE_SIZE)))}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setParam('offset', String(offset + PAGE_SIZE))}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <RunDetailDialog run={selectedRun} open={dialogOpen} onOpenChange={setDialogOpen} />

      {tasksRun && (
        <CoverageTasksDialog
          matterId={tasksRun.matter_id}
          coverageId={tasksRun.coverage_id}
          coverageName={tasksRun.coverage_name}
          open={tasksDialogOpen}
          onOpenChange={setTasksDialogOpen}
        />
      )}

      <Dialog
        open={restartDialogOpen}
        onOpenChange={(open) => {
          setRestartDialogOpen(open);
          if (!open) {
            setRestartRun(null);
            setRestartInstructions('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {restartRun?.status === 'running'
                ? 'Stop & Restart Crashing Out'
                : 'Restart Crashing Out'}
            </DialogTitle>
          </DialogHeader>
          {restartRun && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-600">
                <span className="font-medium text-zinc-800">{restartRun.matter_name}</span>
                {' — '}
                {restartRun.coverage_name}
              </div>
              {restartRun.status === 'running' && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  This will stop the currently running workflow before starting a new one.
                </div>
              )}
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-zinc-700">
                  Extra instructions (optional)
                </p>
                <Textarea
                  placeholder="Add any extra instructions for this run..."
                  value={restartInstructions}
                  onChange={(event) => setRestartInstructions(event.target.value)}
                  rows={4}
                  className="text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRestartDialogOpen(false)}
                  disabled={isRestarting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleRestartRun}
                  disabled={isRestarting}
                  className="gap-1.5"
                >
                  <RotateCw className={cn('h-3.5 w-3.5', isRestarting && 'animate-spin')} />
                  {isRestarting
                    ? restartRun.status === 'running'
                      ? 'Stopping & Restarting...'
                      : 'Restarting...'
                    : restartRun.status === 'running'
                      ? 'Stop & Restart'
                      : 'Restart'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PostgresQuantModePageContent: React.FC<{
  source: CrashingOutSource;
  setSource: (nextSource: CrashingOutSource) => void;
}> = ({ source, setSource }) => {
  const user = useSelector(selectUser);
  const isEnabled = isCrashingOutEnabledForUser(user?.email);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRun, setSelectedRun] = useState<QuantModeRun | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tasksRun, setTasksRun] = useState<QuantModeRun | null>(null);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);
  const [restartRun, setRestartRun] = useState<QuantModeRun | null>(null);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [restartInstructions, setRestartInstructions] = useState('');
  const [isRestarting, setIsRestarting] = useState(false);
  const [instructionsTarget, setInstructionsTarget] = useState<{
    matterId: string;
    coverageId: string;
    coverageName: string;
  } | null>(null);
  const [instructionsInput, setInstructionsInput] = useState('');
  const [startingCoverageKey, setStartingCoverageKey] = useState<string | null>(null);
  const quantModeSource: CrashingOutSource = 'postgres';

  const status = searchParams.get('status') || '';
  const userId = searchParams.get('user_id') || '';
  const taskAssigneeId = searchParams.get('task_assignee_id') || '';
  const startedAfter = searchParams.get('started_after') ?? DEFAULT_STARTED_AFTER;
  const startedBefore = searchParams.get('started_before') ?? DEFAULT_STARTED_BEFORE;
  const offset = parseInt(searchParams.get('offset') || '0', 10);

  const queryParams = useMemo(
    () => ({
      status: status || undefined,
      user_id: userId || undefined,
      task_assignee_id: taskAssigneeId || undefined,
      started_after: startedAfter || undefined,
      started_before: startedBefore || undefined,
      limit: PAGE_SIZE,
      offset,
      source: quantModeSource,
    }),
    [status, userId, taskAssigneeId, startedAfter, startedBefore, offset, quantModeSource],
  );

  const { data: staffUsers = [] } = useGetStaffUsersQuery(undefined, { skip: !isEnabled });

  const sortedStaffUsers = useMemo(
    () =>
      [...staffUsers].sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
      ),
    [staffUsers],
  );

  const { data, isLoading, isFetching, error, refetch } = useGetQuantModeRunsQuery(queryParams, {
    skip: !isEnabled,
    pollingInterval: !isEnabled ? 0 : 10000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [cancelCrashingOut] = useCancelCrashingOutMutation();
  const [startCrashingOut] = useStartCrashingOutMutation();
  const [stoppingRunId, setStoppingRunId] = useState<string | null>(null);

  const handleStopRun = useCallback(
    async (run: QuantModeRun) => {
      if (!run.matter_id || !run.coverage_id) return;
      setStoppingRunId(run.run_id);
      try {
        await cancelCrashingOut({
          matterId: run.matter_id,
          coverageId: run.coverage_id,
          request: { reason: 'Stopped from Quant Mode' },
        }).unwrap();
        toast.success('Workflow stopped');
        refetch();
      } catch {
        toast.error('Failed to stop workflow');
      } finally {
        setStoppingRunId(null);
      }
    },
    [cancelCrashingOut, refetch],
  );

  const startRunForCoverage = useCallback(
    async (matterId: string, coverageId: string, instructions?: string): Promise<boolean> => {
      const key = `${matterId}:${coverageId}`;
      setStartingCoverageKey(key);
      try {
        const trimmedInstructions = instructions?.trim();
        const response = await startCrashingOut({
          matterId,
          coverageId,
          request: trimmedInstructions ? { instructions: trimmedInstructions } : {},
        }).unwrap();
        toast.success(response.message || 'Crashing Out started');
        refetch();
        return true;
      } catch {
        toast.error('Failed to start Crashing Out');
        return false;
      } finally {
        setStartingCoverageKey((current) => (current === key ? null : current));
      }
    },
    [startCrashingOut, refetch],
  );

  const handleOpenStartWithInstructions = useCallback(
    (target: { matterId: string; coverageId: string; coverageName: string }) => {
      setInstructionsTarget(target);
      setInstructionsInput('');
    },
    [],
  );

  const handleSubmitStartWithInstructions = useCallback(async () => {
    if (!instructionsTarget) return;
    const trimmed = instructionsInput.trim();
    if (!trimmed) {
      toast.error('Instructions are required');
      return;
    }
    const started = await startRunForCoverage(
      instructionsTarget.matterId,
      instructionsTarget.coverageId,
      trimmed,
    );
    if (started) {
      setInstructionsTarget(null);
      setInstructionsInput('');
    }
  }, [instructionsTarget, instructionsInput, startRunForCoverage]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    // Reset offset when changing filters
    if (key !== 'offset') {
      next.delete('offset');
    }
    setSearchParams(next);
  };

  const handleOpenRunDetail = (run: QuantModeRun) => {
    setSelectedRun(run);
    setDialogOpen(true);
  };

  const handleOpenRestartDialog = (run: QuantModeRun) => {
    setRestartRun(run);
    setRestartInstructions('');
    setRestartDialogOpen(true);
  };

  const handleRestartRun = useCallback(async () => {
    if (!restartRun?.matter_id || !restartRun?.coverage_id) return;
    setIsRestarting(true);
    let didCancel = false;
    try {
      // If the run is currently running, cancel it first
      if (restartRun.status === 'running') {
        await cancelCrashingOut({
          matterId: restartRun.matter_id,
          coverageId: restartRun.coverage_id,
          request: { reason: 'Restarted from Quant Mode with new instructions' },
        }).unwrap();
        didCancel = true;
      }
      const request = restartInstructions.trim()
        ? { instructions: restartInstructions.trim() }
        : {};
      await startCrashingOut({
        matterId: restartRun.matter_id,
        coverageId: restartRun.coverage_id,
        request,
      }).unwrap();
      toast.success('Crashing Out restarted');
      setRestartDialogOpen(false);
      setRestartRun(null);
      setRestartInstructions('');
      refetch();
    } catch {
      if (didCancel) {
        toast.error('Workflow was stopped but failed to start a new one. Please restart manually.');
        refetch();
      } else {
        toast.error('Failed to restart Crashing Out');
      }
    } finally {
      setIsRestarting(false);
    }
  }, [restartRun, restartInstructions, cancelCrashingOut, startCrashingOut, refetch]);

  const total = data?.count || 0;
  const showingStart = total > 0 ? offset + 1 : 0;
  const showingEnd = Math.min(offset + PAGE_SIZE, total);

  // Group runs by coverage (matter_id + coverage_id)
  const groupedByCoverage = useMemo(() => {
    const groups = new Map<
      string,
      { coverageId: string; matterId: string; matterName: string; runs: QuantModeRun[] }
    >();
    (data?.results || []).forEach((run) => {
      const key = `${run.matter_id}:${run.coverage_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          coverageId: run.coverage_id,
          matterId: run.matter_id,
          matterName: run.matter_name,
          runs: [],
        });
      }
      const group = groups.get(key);
      if (group) group.runs.push(run);
    });
    return Array.from(groups.values());
  }, [data?.results]);

  if (!isEnabled) {
    return <Navigate to="/matters" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-900">Quant Mode</h1>
          <p className="text-sm text-zinc-600">
            Monitor all Crashing Out runs across matters.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TemporalSourceSelector source={source} setSource={setSource} />
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            Refresh
          </Button>
          <Link to="/quant-mode/config">
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
              <Settings className="h-3.5 w-3.5" />
              Config
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {/* Status pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant={!status ? 'default' : 'outline'}
            className="h-7 text-xs"
            onClick={() => setParam('status', '')}
          >
            All
          </Button>
          {STATUS_OPTIONS.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={status === s ? 'default' : 'outline'}
              className="h-7 text-xs capitalize"
              onClick={() => setParam('status', status === s ? '' : s)}
            >
              {s.replace('_', ' ')}
            </Button>
          ))}
        </div>

        {/* Assignee filter */}
        <Select
          value={userId || 'all'}
          onValueChange={(value) => setParam('user_id', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="h-7 w-36 sm:w-48 text-xs">
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {sortedStaffUsers.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Open Claim assignee filter */}
        <Select
          value={taskAssigneeId || 'all'}
          onValueChange={(value) => setParam('task_assignee_id', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="h-7 w-36 sm:w-48 text-xs">
            <SelectValue placeholder="OC Assignee: All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">OC Assignee: All</SelectItem>
            {sortedStaffUsers.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date range */}
        <Input
          type="date"
          value={startedAfter}
          onChange={(e) => setParam('started_after', e.target.value)}
          className="h-7 w-32 sm:w-36 text-xs"
          placeholder="From"
        />
        <Input
          type="date"
          value={startedBefore}
          onChange={(e) => setParam('started_before', e.target.value)}
          className="h-7 w-32 sm:w-36 text-xs"
          placeholder="To"
        />
      </div>

      {/* Coverage-grouped runs */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load Quant Mode data. Check the browser console for details.
        </div>
      ) : (
        <>
          {groupedByCoverage.length === 0 ? (
            <div className="rounded border border-zinc-200 p-8 text-center text-zinc-500 text-sm">
              No runs found.
            </div>
          ) : (
            <div className="space-y-6">
              {groupedByCoverage.map((group) => (
                <PostgresCoverageRunGroup
                  key={`${group.matterId}:${group.coverageId}`}
                  coverageId={group.coverageId}
                  matterId={group.matterId}
                  matterName={group.matterName}
                  runs={group.runs}
                  onStartRun={(matterId, coverageId) => {
                    startRunForCoverage(matterId, coverageId);
                  }}
                  onStartRunWithInstructions={handleOpenStartWithInstructions}
                  isStarting={startingCoverageKey === `${group.matterId}:${group.coverageId}`}
                  onOpenRunDetail={handleOpenRunDetail}
                  onOpenTasks={(run) => {
                    setTasksRun(run);
                    setTasksDialogOpen(true);
                  }}
                  onStopRun={handleStopRun}
                  onRestartRun={handleOpenRestartDialog}
                  stoppingRunId={stoppingRunId}
                  isFetching={isFetching}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">
                {showingStart}–{showingEnd} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  disabled={offset === 0}
                  onClick={() => setParam('offset', String(Math.max(0, offset - PAGE_SIZE)))}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setParam('offset', String(offset + PAGE_SIZE))}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Run detail dialog */}
      {selectedRun ? (
        <CrashingOutRunDetailsModal
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          matterId={selectedRun.matter_id}
          coverageId={selectedRun.coverage_id}
          preferredRunId={selectedRun.run_id}
          initialDisplayStatus={selectedRun.status}
          initialMatterName={selectedRun.matter_name}
          initialCoverageName={selectedRun.coverage_name}
          initialInitiatedByName={selectedRun.initiated_by_user_name}
        />
      ) : null}

      {/* Coverage tasks dialog */}
      {tasksRun && (
        <CoverageTasksDialog
          matterId={tasksRun.matter_id}
          coverageId={tasksRun.coverage_id}
          coverageName={tasksRun.coverage_name}
          open={tasksDialogOpen}
          onOpenChange={setTasksDialogOpen}
        />
      )}

      <Dialog
        open={Boolean(instructionsTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setInstructionsTarget(null);
            setInstructionsInput('');
          }
        }}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">Start Run with Instructions</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">{instructionsTarget?.coverageName}</p>
            <Textarea
              value={instructionsInput}
              onChange={(event) => setInstructionsInput(event.target.value)}
              placeholder="Add extra guidance for this run..."
              className="min-h-[120px]"
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setInstructionsTarget(null);
                  setInstructionsInput('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={
                  !instructionsTarget
                  || startingCoverageKey
                    === `${instructionsTarget.matterId}:${instructionsTarget.coverageId}`
                }
                onClick={() => {
                  handleSubmitStartWithInstructions();
                }}
              >
                {instructionsTarget
                && startingCoverageKey
                  === `${instructionsTarget.matterId}:${instructionsTarget.coverageId}`
                  ? 'Starting...'
                  : 'Start Run'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={restartDialogOpen}
        onOpenChange={(open) => {
          setRestartDialogOpen(open);
          if (!open) {
            setRestartRun(null);
            setRestartInstructions('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {restartRun?.status === 'running'
                ? 'Stop & Restart Crashing Out'
                : 'Restart Crashing Out'}
            </DialogTitle>
          </DialogHeader>
          {restartRun && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-600">
                <span className="font-medium text-zinc-800">
                  {restartRun.matter_name}
                </span>
                {' — '}
                {restartRun.coverage_name}
              </div>
              {restartRun.status === 'running' && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  This will stop the currently running workflow before starting a new one.
                </div>
              )}
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-zinc-700">
                  Extra instructions (optional)
                </p>
                <Textarea
                  placeholder="Add any extra instructions for this run..."
                  value={restartInstructions}
                  onChange={(event) => setRestartInstructions(event.target.value)}
                  rows={4}
                  className="text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRestartDialogOpen(false)}
                  disabled={isRestarting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleRestartRun}
                  disabled={isRestarting}
                  className="gap-1.5"
                >
                  <RotateCw
                    className={cn('h-3.5 w-3.5', isRestarting && 'animate-spin')}
                  />
                  {isRestarting
                    ? restartRun.status === 'running'
                      ? 'Stopping & Restarting...'
                      : 'Restarting...'
                    : restartRun.status === 'running'
                      ? 'Stop & Restart'
                      : 'Restart'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const QuantModePage: React.FC = () => {
  const { source, setSource } = useCrashingOutSource();

  if (source === 'temporal') {
    return (
      <>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4 sm:pt-6">
          <div className="flex justify-end">
            <TemporalSourceSelector source={source} setSource={setSource} />
          </div>
        </div>
        <TemporalQuantModePageContent />
      </>
    );
  }

  return <PostgresQuantModePageContent source={source} setSource={setSource} />;
};
